// PriceBreakdown — Right sidebar component: fare, taxes, seat upgrade, insurance, total.
// Accepts all line items explicitly; no internal state.

import * as React from 'react';
import { cn } from '@/lib/cn';
import {
  formatPaise,
  AIRPORT_TAXES_PAISE,
  PLATFORM_FEE_PAISE,
  INSURANCE_PAISE,
} from '@/lib/booking-mock';

interface LineItem {
  label:    string;
  paise:    number;
  positive?: boolean; // adds a "+" prefix on positive extras
}

interface Props {
  fareLabel:         string;            // e.g. "Singapore Airlines · Standard"
  routeLabel:        string;            // e.g. "BLR → DPS · Mar 17 · 11h 15m"
  fareBasePaise:     number;
  seatUpgradePaise?: number;
  showInsurance?:    boolean;
  extraItems?:       LineItem[];        // any additional line items
  /** Render below the total — usually the CTA button. */
  cta?:              React.ReactNode;
  ctaNote?:          string;
  sectionTitle?:     string;           // defaults to "YOUR SELECTION"
}

export function PriceBreakdown({
  fareLabel,
  routeLabel,
  fareBasePaise,
  seatUpgradePaise = 0,
  showInsurance    = false,
  extraItems       = [],
  cta,
  ctaNote,
  sectionTitle = 'YOUR SELECTION',
}: Props) {
  const taxesPaise   = AIRPORT_TAXES_PAISE;
  const platformPaise = PLATFORM_FEE_PAISE;
  const insurancePaise = showInsurance ? INSURANCE_PAISE : 0;

  const totalPaise =
    fareBasePaise +
    taxesPaise +
    platformPaise +
    seatUpgradePaise +
    insurancePaise +
    extraItems.reduce((s, it) => s + it.paise, 0);

  const rows: Array<{ label: string; display: string; muted?: boolean }> = [
    { label: 'Base fare (1 adult)', display: formatPaise(fareBasePaise) },
    { label: 'Airport taxes',       display: formatPaise(taxesPaise) },
    { label: 'Platform fee',        display: formatPaise(platformPaise), muted: true },
    ...(seatUpgradePaise > 0
      ? [{ label: 'Seat upgrade', display: `+${formatPaise(seatUpgradePaise)}` }]
      : []),
    ...(showInsurance
      ? [{ label: 'Travel insurance', display: `+${formatPaise(insurancePaise)}` }]
      : []),
    ...extraItems.map(it => ({
      label:   it.label,
      display: (it.positive ? '+' : '') + formatPaise(it.paise),
    })),
  ];

  return (
    <div className="bg-[#131C30] rounded-[24px] border border-white/[0.07] p-6">
      {/* Section eyebrow */}
      <div
        className="text-[11px] font-semibold tracking-[1.5px] text-[#8C98B0] mb-4"
        style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
      >
        {sectionTitle}
      </div>

      {/* Flight summary */}
      <div className="pb-4 border-b border-white/[0.07]">
        <div
          className="text-[15px] font-bold text-[#F4F3F0]"
          style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
        >
          {fareLabel}
        </div>
        <div
          className="text-[12px] text-[#8C98B0] mt-1"
          style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
        >
          {routeLabel}
        </div>
      </div>

      {/* Line items */}
      <div className="pt-3 pb-2">
        {rows.map(row => (
          <div
            key={row.label}
            className="flex justify-between py-1"
          >
            <span
              className="text-[13px] text-[#8C98B0]"
              style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
            >
              {row.label}
            </span>
            <span
              className={cn(
                'text-[13px] font-semibold',
                row.muted ? 'text-[#4E5E78]' : 'text-[#F4F3F0]',
              )}
              style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
            >
              {row.display}
            </span>
          </div>
        ))}
      </div>

      {/* Total */}
      <div
        className="flex justify-between pt-3 mt-1 border-t border-white/[0.07]"
      >
        <span
          className="text-[20px] font-bold text-[#F4F3F0]"
          style={{ fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif' }}
        >
          Total
        </span>
        <span
          className="text-[20px] font-bold text-[#F4F3F0]"
          style={{ fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif' }}
        >
          {formatPaise(totalPaise)}
        </span>
      </div>

      <div
        className="text-[11px] text-[#4E5E78] mb-4"
        style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
      >
        per person · all taxes included
      </div>

      {/* CTA slot */}
      {cta}

      {/* CTA note */}
      {ctaNote && (
        <div
          className="text-[11px] text-[#8C98B0] text-center mt-2.5"
          style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
        >
          {ctaNote}
        </div>
      )}
    </div>
  );
}
